import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useNavigate } from "react-router-dom";
import { BarChart3, Star, Check, X, Users } from "lucide-react";

const ScoreBar = ({ score, maxScore = 5, color = "bg-blue-500" }) => {
  const pct = (score / maxScore) * 100;
  return (
    <div className="w-full h-2.5 rounded-full bg-zinc-800 overflow-hidden">
      <div
        className={`h-full rounded-full ${color} transition-all duration-500`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
};

const ReviewerBar = ({ reviewer, maxScore = 5 }) => {
  const pct = (reviewer.score / maxScore) * 100;
  const color = reviewer.decision === "accept" ? "bg-emerald-500" : "bg-red-500";
  return (
    <div className="flex items-center gap-3 group">
      <span className="text-xs text-zinc-500 w-24 truncate text-right" title={reviewer.reviewer_name}>
        {reviewer.reviewer_name}
      </span>
      <div className="flex-1 h-2 rounded-full bg-zinc-800 overflow-hidden">
        <div
          className={`h-full rounded-full ${color} transition-all duration-500`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="font-mono text-xs text-zinc-400 w-8">{reviewer.score}/{maxScore}</span>
      {reviewer.decision === "accept" ? (
        <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
      ) : (
        <X className="w-3.5 h-3.5 text-red-400 shrink-0" />
      )}
    </div>
  );
};

export default function ScoresDashboard({ user }) {
  const navigate = useNavigate();
  const [scores, setScores] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const resp = await api.get("/roles");
        setRoles(resp.data);
      } catch (e) {}
    };
    fetchRoles();
  }, []);

  useEffect(() => {
    const fetchScores = async () => {
      setLoading(true);
      try {
        const params = selectedRole && selectedRole !== "all" ? `?role_id=${selectedRole}` : "";
        const resp = await api.get(`/candidates/scores${params}`);
        setScores(resp.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchScores();
  }, [selectedRole]);

  return (
    <div className="px-4 py-6 sm:p-6 md:p-8 lg:p-12 space-y-6 animate-fade-in" data-testid="scores-dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white uppercase">Scores</h1>
          <p className="text-zinc-500 mt-1 text-sm">Candidate scores with individual reviewer breakdown</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedRole} onValueChange={setSelectedRole}>
            <SelectTrigger className="w-44 sm:w-56 bg-zinc-800 border-white/10 text-zinc-300 text-sm" data-testid="scores-role-filter">
              <SelectValue placeholder="All Roles" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-white/10">
              <SelectItem value="all">All Roles</SelectItem>
              {roles.map((r) => (
                <SelectItem key={r.role_id} value={r.role_id}>{r.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Badge variant="secondary" className="font-mono bg-zinc-800 border border-white/10 text-zinc-400 text-xs shrink-0">
            {scores.length}
          </Badge>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : scores.length === 0 ? (
        <div className="text-center py-20">
          <BarChart3 className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-400">No reviewed candidates yet</p>
          <p className="text-sm text-zinc-600 mt-1">Start reviewing candidates to see scores here</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="scores-list">
          {scores.map((candidate) => (
            <Card
              key={candidate.candidate_id}
              className="bg-zinc-900/50 border-white/10 hover:border-white/20 cursor-pointer transition-colors"
              data-testid={`score-card-${candidate.candidate_id}`}
              onClick={() => navigate(`/candidates/${candidate.candidate_id}`)}
            >
              <CardContent className="p-4 lg:p-5">
                <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-6">
                  {/* Left: Candidate info + avg score */}
                  <div className="md:w-56 lg:w-64 shrink-0">
                    <div className="flex items-center gap-3 mb-2">
                      <div>
                        <h3 className="text-sm lg:text-base font-semibold text-white">{candidate.name}</h3>
                        {candidate.greenhouse_role && (
                          <p className="text-xs text-zinc-500">{candidate.greenhouse_role}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 mt-2">
                      <div className="text-center">
                        <p className="font-mono text-xl lg:text-2xl font-bold text-white">{candidate.avg_score}</p>
                        <p className="text-[10px] text-zinc-500">avg</p>
                      </div>
                      <ScoreBar score={candidate.avg_score} color={
                        candidate.avg_score >= 4 ? "bg-emerald-500" :
                        candidate.avg_score >= 3 ? "bg-blue-500" :
                        candidate.avg_score >= 2 ? "bg-amber-500" : "bg-red-500"
                      } />
                      <div className="flex items-center gap-1 shrink-0">
                        <Users className="w-3.5 h-3.5 text-zinc-500" />
                        <span className="font-mono text-xs text-zinc-400">{candidate.review_count}</span>
                      </div>
                    </div>
                  </div>

                  {/* Right: Individual reviewer scores */}
                  <div className="flex-1 space-y-1.5 min-w-0">
                    <p className="text-[10px] lg:text-xs text-zinc-500 uppercase tracking-wider mb-1.5">Reviewer Scores</p>
                    {candidate.reviewer_scores.map((reviewer, i) => (
                      <ReviewerBar key={i} reviewer={reviewer} />
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
